import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'

import apiClient from '@/libs/apiClient'
import type { Product } from '@/types/catalog'

// ===============================
// Pagination
// ===============================
interface Pagination {
  currentPage: number
  lastPage: number
  perPage: number
  total: number
}

interface ProductState {
  products: Product[]
  product: Product | null
  pagination: Pagination | null
  loading: boolean
  error: string | null
  latestFetchRequestId: string | null
}

const initialState: ProductState = {
  products: [],
  product: null,
  pagination: null,
  loading: false,
  error: null,
  latestFetchRequestId: null
}

// ===============================
// Thunks
// ===============================

// Fetch products
export const fetchProducts = createAsyncThunk<
  { products: Product[]; pagination: Pagination },
  { limit?: number; categoryId?: string } | undefined,
  { rejectValue: string }
>('products/fetchProducts', async (params = {}, { rejectWithValue }) => {
  try {
    const limit = params.limit || 1000
    const queryParams: Record<string, string | number> = { limit }

    if (params.categoryId) queryParams.category_id = params.categoryId

    const res = await apiClient.get('/admin/v1/products', { params: queryParams })
    const json = res.data

    const products: Product[] = json.data

    const pagination: Pagination = {
      currentPage: json.meta.currentPage,
      lastPage: json.meta.lastPage,
      perPage: json.meta.perPage,
      total: json.meta.total
    }

    return { products, pagination }
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || err.message || 'Failed to fetch products')
  }
})

// ADD Product
export const addProduct = createAsyncThunk<Product, FormData, { rejectValue: string }>(
  'products/addProduct',
  async (formData, { rejectWithValue }) => {
    try {
      const res = await apiClient.post('/admin/v1/products/add', formData)
      const json = res.data

      return json.data as Product
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to add product')
    }
  }
)

// Edit Product
export const editProduct = createAsyncThunk<Product, FormData, { rejectValue: string }>(
  'products/editProduct',
  async (formData, { rejectWithValue }) => {
    try {
      const id = formData.get('productId')
      const res = await apiClient.post(`/admin/v1/products/${id}/edit`, formData)
      const json = res.data

      return json.data as Product
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to edit product')
    }
  }
)

// Product details
export const fetchProductDetails = createAsyncThunk<Product, string, { rejectValue: string }>(
  'products/details',
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiClient.get(`/admin/v1/products/${id}`)
      const json = res.data

      return json.data
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to fetch product details')
    }
  }
)

// Toggle Status
export const toggleStatusProduct = createAsyncThunk<string, { id: string; status: string }, { rejectValue: string }>(
  'products/enable',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      await apiClient.post(`/admin/v1/products/${id}/${status}`)

      return id
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to toggle product status')
    }
  }
)

// Delete Product
export const deleteProduct = createAsyncThunk<string, string, { rejectValue: string }>(
  'products/delete',
  async (id, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/admin/v1/products/${id}/delete`)

      return id
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to delete product')
    }
  }
)

// ===============================
// Slice
// ===============================
const productSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    selectProduct: (state, action) => {
      state.product = action.payload
    }
  },
  extraReducers: builder => {
    builder

      // FETCH
      .addCase(fetchProducts.pending, (state, action) => {
        state.loading = true
        state.latestFetchRequestId = action.meta.requestId
      })
      .addCase(
        fetchProducts.fulfilled,
        (state, action: PayloadAction<{ products: Product[]; pagination: Pagination }>) => {
          // Ignore an older, slower request (e.g. for a category filter the
          // user has since changed away from) resolving after a newer one —
          // otherwise it can silently overwrite the correct, current results.
          if (action.meta.requestId !== state.latestFetchRequestId) return

          state.loading = false
          state.products = action.payload.products
          state.pagination = action.payload.pagination
        }
      )
      .addCase(fetchProducts.rejected, (state, action) => {
        if (action.meta.requestId !== state.latestFetchRequestId) return

        state.loading = false
        state.error = action.payload || 'Failed to load products'
      })

      // ADD
      .addCase(addProduct.fulfilled, (state, action) => {
        state.products.push(action.payload)
      })

      // EDIT
      .addCase(editProduct.fulfilled, (state, action) => {
        const updated = action.payload

        state.products = state.products.map(p => (p.id === updated.id ? updated : p))
      })

      // DETAILS
      .addCase(fetchProductDetails.fulfilled, (state, action) => {
        state.product = action.payload
      })

      // Toggle
      .addCase(toggleStatusProduct.fulfilled, (state, action) => {
        state.products = state.products.map(p => (p.id === action.payload ? { ...p, is_enabled: !p.is_enabled } : p))
      })

      // Delete
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.products = state.products.filter(p => p.id !== action.payload)
        toast.success('Product deleted')
      })

    // ENABLE
    // .addCase(enableProduct.fulfilled, (state, action) => {
    //   state.products = state.products.map(p => (p.id === action.payload ? { ...p, isEnabled: true } : p))
    // })

    // DISABLE
    // .addCase(disableProduct.fulfilled, (state, action) => {
    //   state.products = state.products.map(p => (p.id === action.payload ? { ...p, isEnabled: false } : p))
    // })
  }
})

export default productSlice.reducer
